<script setup lang="tsx">
import {
	PermAdminCustomFields,
	PermAdminMeasureImport,
	PermAdminBackup,
	PermAdminInstances,
	PermAdminJira,
	PermAdminRoles,
	PermAdminSecurityNeed,
	PermAdminStandardUpgrade,
	PermAdminUsers,
	PermOwnRiskRead,
	PermAllRiskRead,
	PermOwnRiskWrite,
	PermAllRiskWrite,
	PermOwnRiskDelete,
	PermAllRiskDelete,
	PermRiskAdd,
	PermAllAssetDelete,
	PermAllAssetRead,
	PermAllAssetWrite,
	PermAllBPDelete,
	PermAllBPRead,
	PermAllBPWrite,
	PermAllDataAssetDelete,
	PermAllDataAssetRead,
	PermAllDataAssetWrite,
	PermAllJiraDelete,
	PermAllJiraExport,
	PermAllJiraRead,
	PermAllMeasureRead,
	PermAllMeasureWrite,
	PermAllModuleDelete,
	PermAllModuleRead,
	PermAllModuleWrite,
	PermAssetAdd,
	PermBasicAudit,
	PermInternalAudit,
	PermBPAdd,
	PermDataAssetAdd,
	PermModuleAdd,
	PermOwnAssetDelete,
	PermOwnAssetRead,
	PermOwnAssetWrite,
	PermOwnBPDelete,
	PermOwnBPRead,
	PermOwnBPWrite,
	PermOwnDataAssetDelete,
	PermOwnDataAssetRead,
	PermOwnDataAssetWrite,
	PermOwnJiraDelete,
	PermOwnJiraExport,
	PermOwnJiraRead,
	PermOwnMeasureRead,
	PermOwnMeasureWrite,
	PermOwnModuleDelete,
	PermOwnModuleRead,
	PermOwnModuleWrite,
	PermDocumentAdd,
	PermDocumentDelete,
	PermDocumentRead,
	PermDocumentWrite,
	type Permission,
	PermAdminRiskSettings,
	PermAuditRead,
} from '@/models/enums-generated'
import PermissionSet from './PermissionSet.vue'
import PermissionSet2 from './PermissionSet2.vue'
import iconPath from '@/assets/images/jira-1.svg'

const props = defineProps<{ readOnly: boolean }>()
const model = defineModel<Permission[]>({ required: true })
</script>

<template>
	<table class="w-100">
		<tr class="text-center">
			<th></th>
			<th>{{ $t('misc.view') }}</th>
			<th>{{ $t('misc.edit') }}</th>
			<th>{{ $t('misc.add') }}</th>
			<th>{{ $t('misc.delete') }}</th>
		</tr>

		<tr class="text-center permission-row">
			<td class="text-start">
				<h6><i class="bi bi-folder2 icon"></i> {{ $t('businessProcesses') }}</h6>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllBPRead" :own="PermOwnBPRead" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllBPWrite" :own="PermOwnBPWrite" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermBPAdd" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllBPDelete" :own="PermOwnBPDelete" :readOnly="props.readOnly"></PermissionSet>
			</td>
		</tr>

		<tr class="text-center">
			<td class="text-start">
				<h6><i class="bi bi-clipboard icon"></i> {{ $t('assets') }}</h6>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllAssetRead" :own="PermOwnAssetRead" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllAssetWrite" :own="PermOwnAssetWrite" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAssetAdd" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllAssetDelete" :own="PermOwnAssetDelete" :readOnly="props.readOnly"></PermissionSet>
			</td>
		</tr>

		<tr class="text-center permission-row">
			<td class="text-start">
				<h6><i class="bi bi-database icon"></i> {{ $t('dataAssets') }}</h6>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllDataAssetRead" :own="PermOwnDataAssetRead" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllDataAssetWrite" :own="PermOwnDataAssetWrite" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermDataAssetAdd" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet
					v-model="model"
					:all="PermAllDataAssetDelete"
					:own="PermOwnDataAssetDelete"
					:readOnly="props.readOnly"
				></PermissionSet>
			</td>
		</tr>

		<tr class="text-center">
			<td class="text-start">
				<h6><i class="bi bi-shield-lock icon"></i> {{ $t('measure', 2) }}</h6>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllMeasureRead" :own="PermOwnMeasureRead" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllMeasureWrite" :own="PermOwnMeasureWrite" :readOnly="props.readOnly"></PermissionSet>
			</td>
		</tr>

		<tr class="text-center permission-row">
			<td class="text-start">
				<h6><i class="bi bi-layers icon"></i> {{ $t('module.label', 2) }}</h6>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllModuleRead" :own="PermOwnModuleRead" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllModuleWrite" :own="PermOwnModuleWrite" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermModuleAdd" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllModuleDelete" :own="PermOwnModuleDelete" :readOnly="props.readOnly"></PermissionSet>
			</td>
		</tr>

		<tr class="text-center">
			<td class="text-start">
				<h6><img :src="iconPath" alt="icon" class="me-1 jira-icon" />{{ $t('jira.label') }}</h6>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllJiraRead" :own="PermOwnJiraRead" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllJiraExport" :own="PermOwnJiraExport" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td></td>
			<td>
				<PermissionSet v-model="model" :all="PermAllJiraDelete" :own="PermOwnJiraDelete" :readOnly="props.readOnly"></PermissionSet>
			</td>
		</tr>

		<tr class="text-center permission-row">
			<td class="text-start">
				<h6><i class="bi-exclamation-triangle icon"></i> {{ $t('risk.riskManagement') }}</h6>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllRiskRead" :own="PermOwnRiskRead" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllRiskWrite" :own="PermOwnRiskWrite" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermRiskAdd" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermAllRiskDelete" :own="PermOwnRiskDelete" :readOnly="props.readOnly"></PermissionSet>
			</td>
		</tr>

		<tr class="text-center">
			<td class="text-start">
				<h6><i class="bi bi-folder2 icon"></i> {{ $t('document.label', 2) }}</h6>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermDocumentRead" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermDocumentWrite" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermDocumentAdd" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td>
				<PermissionSet v-model="model" :all="PermDocumentDelete" :readOnly="props.readOnly"></PermissionSet>
			</td>
		</tr>

		<br />
		<tr class="text-center permission-row">
			<td class="text-start">
				<h6><i class="bi bi-clipboard-check icon"></i> {{ $t('audit.label') }}</h6>
			</td>
			<td></td>
			<td>
				<PermissionSet v-model="model" :all="PermAuditRead" :title="$t('misc.view')" :readOnly="props.readOnly"></PermissionSet>
			</td>
			<td></td>
			<td>
				<PermissionSet2
					v-model="model"
					:own="PermBasicAudit"
					:all="PermInternalAudit"
					:title="$t('audit.basicAuditor')"
					:title2="$t('audit.internalAuditor')"
					:readOnly="props.readOnly"
				></PermissionSet2>
			</td>
		</tr>
		<br />

		<tr class="text-center">
			<td class="text-start">
				<h6><i class="bi bi-gear icon"></i> Admin</h6>
			</td>
			<td></td>
			<td>
				<div class="mb-2 pt-2">
					<PermissionSet v-model="model" :all="PermAdminInstances" :title="$t('institution', 2)" :readOnly="props.readOnly"></PermissionSet>
				</div>
				<div class="mb-2">
					<PermissionSet v-model="model" :all="PermAdminUsers" :title="$t('user.label', 2)" :readOnly="props.readOnly"></PermissionSet>
				</div>
				<div class="mb-2">
					<PermissionSet v-model="model" :all="PermAdminRoles" :title="$t('role', 2)" :readOnly="props.readOnly"></PermissionSet>
				</div>
				<div class="mb-2">
					<PermissionSet v-model="model" :all="PermAdminJira" :title="$t('jira.label')" :readOnly="props.readOnly"></PermissionSet>
				</div>
			</td>
			<td></td>
			<td>
				<div class="mb-2">
					<PermissionSet
						v-model="model"
						:all="PermAdminSecurityNeed"
						:title="$t('securityNeed.label')"
						:readOnly="props.readOnly"
					></PermissionSet>
				</div>
				<div class="mb-2">
					<PermissionSet
						v-model="model"
						:all="PermAdminStandardUpgrade"
						:title="$t('standardUpgrade')"
						:readOnly="props.readOnly"
					></PermissionSet>
				</div>
				<div class="mb-2">
					<PermissionSet
						v-model="model"
						:all="PermAdminCustomFields"
						:title="$t('customFields')"
						:readOnly="props.readOnly"
					></PermissionSet>
				</div>
				<div class="mb-2">
					<PermissionSet
						v-model="model"
						:all="PermAdminRiskSettings"
						:title="$t('risk.tabs.riskSettings')"
						:readOnly="props.readOnly"
					></PermissionSet>
				</div>
				<div class="mb-2">
					<PermissionSet
						v-model="model"
						:all="PermAdminMeasureImport"
						:title="$t('measureImport')"
						:readOnly="props.readOnly"
					></PermissionSet>
				</div>
				<div class="mb-2">
					<PermissionSet v-model="model" :all="PermAdminBackup" :title="$t('backup')" :readOnly="props.readOnly"></PermissionSet>
				</div>
			</td>
		</tr>
	</table>
</template>

<style lang="sass" scoped>
.permission-row
  background-color: var(--bs-secondary-bg)

.icon
  opacity: 50%
  position: relative

.jira-icon
  filter: grayscale(90%)
  width: 18px
  height: 18px
  opacity: 60%
  margin-left: -3px
  margin-top: -4px
  position: relative

h6
  margin-left: 8px
  margin-top: 8px
</style>
